import { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { CloudArrowDown, WarningCircle } from "@phosphor-icons/react";

function timeAgo(iso) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function LastSyncBadge() {
  const [info, setInfo] = useState({ last_sync: null, connected: false, poll_interval: 0, enabled: false });
  const [tick, setTick] = useState(0);
  const timer = useRef(null);
  const tickTimer = useRef(null);

  const fetchInfo = useCallback(async () => {
    try {
      const { data } = await api.get("/interface/last-sync");
      setInfo(data || {});
    } catch (err) {
      // Silent fail — badge just shows "never"
    }
  }, []);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo]);

  // Poll on the same interval as configured
  useEffect(() => {
    if (timer.current) clearInterval(timer.current);
    const interval = Number(info.poll_interval) || 0;
    if (interval > 0 && info.enabled) {
      timer.current = setInterval(fetchInfo, interval * 1000);
    }
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [info.poll_interval, info.enabled, fetchInfo]);

  // Re-render every 10s so "Xm ago" stays fresh
  useEffect(() => {
    tickTimer.current = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(tickTimer.current);
  }, []);

  if (!info.enabled) return null;

  const stale = !info.last_sync;
  const failing = info.last_sync && !info.connected;
  const dotColor = stale ? "bg-[#737373]" : failing ? "bg-[#FF3B30]" : "bg-[#32D74B]";
  const Icon = failing ? WarningCircle : CloudArrowDown;
  const colorText = stale ? "text-[#737373]" : failing ? "text-[#FF3B30]" : "text-[#32D74B]";

  return (
    <div
      className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#0A0A0A] border border-[#262626]"
      title={info.last_sync ? `Last Amano sync: ${new Date(info.last_sync).toLocaleString()}` : "Amano feed never synced"}
      data-testid="last-sync-badge"
      data-tick={tick}
    >
      <span className={`w-2 h-2 rounded-full ${dotColor} ${!stale && !failing ? "animate-pulse" : ""}`} />
      <Icon size={12} weight="bold" className={colorText} />
      <span className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">SYNC</span>
      <span className={`text-[10px] font-mono-data ${colorText}`} data-testid="last-sync-time">
        {timeAgo(info.last_sync)}
      </span>
    </div>
  );
}
